import React, { useState } from 'react';
import {
  Grid,
  Stack,
  Typography,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Box,
  Dialog,
  Button,
  Divider
} from '@mui/material';
import useAuth from '../../hooks/useAuth';
import MainCard from 'components/MainCard';
import ProfileAvatar from 'sections/profiles/ProfileAvatar';
import { getUserProfileFullName } from 'utils/users';
import { User, Rights } from 'graphql/types';
import AddUser from 'sections/users/AddUser';
import ActionConfirmation from 'components/mca/ActionConfirmation';
import { PlusOutlined } from '@ant-design/icons';
import UserInfo from '../users/UserInfo';
import UserRole from '../users/UserRole';

interface Props {
  associatedUsers: User[];
  company?: any;
  usersRefetch?: () => void;
}

const CompanyUserTab = ({ associatedUsers, company, usersRefetch }: Props) => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleUserEdit = (user: User) => {
    if (isSuperAdmin) {
      setSelectedUser(user);
      setDialogOpen(true);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setOpenConfirmation(true);
  };

  const handleSave = () => {
    if (usersRefetch) usersRefetch();
    setDialogOpen(false);
    setSelectedUser(null);
  };

  return (
    <Grid item xs={12}>
      <MainCard border={false} shadow={3} boxShadow sx={{ minHeight: 300 }}>
        <Stack spacing={2}>
          <Box sx={{ height: 200, overflowY: 'auto' }}>
            {associatedUsers.length === 0 ? (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200 
                }}
              >
                <Typography variant="body2">
                  No User Associated
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {associatedUsers.map((user, index) => (
                  <Grid item xs={12} key={'user-' + index}>
                    <ListItem disablePadding divider>
                      <ListItemButton onClick={() => handleUserEdit(user)}>
                        <ListItemText
                          primary={
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                              <UserInfo user={user} />
                              <UserRole user={user} />
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </Grid>
                ))}
              </Box>
            )}
          </Box>
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleAddUser}>
              Add New
            </Button>
          </Grid>
        </Stack>
      </MainCard>

      {dialogOpen && (
        <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
          <AddUser handleAdd={handleAddUser} entity={selectedUser} company={company} onSave={handleSave} onClose={handleClose} open />
        </Dialog>
      )}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        sx={{ '& .MuiDialog-paper': { p: 0 } }}
      >
        <ActionConfirmation
          title="The form will close and changes will be lost. Are you sure you want to proceed?"
          open={openConfirmation}
          useYesNoButtonLabels
          type="error"
          handleClose={(approved: boolean) => {
            setOpenConfirmation(false);
            if (!approved) {
              return;
            }
            setDialogOpen(false);
            setSelectedUser(null);
          }}
        />
      </Dialog>
    </Grid>
  );
};

export default CompanyUserTab;
